import React from "react";
import { Trash2, CalendarClock, User as UserIcon, Lock, RotateCcw } from "lucide-react";
import { TASK_STATUS, statusMeta, isOverdue, formatDue } from "./taskMeta";

const btnCls = (active) =>
  `px-3 h-8 hard text-xs font-bold uppercase tracking-wider border transition-colors ${
    active
      ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]"
      : "bg-white border-[color:var(--border)] hover:border-[color:var(--primary)]"
  }`;

function StatusButtons({ task, onStatus }) {
  return TASK_STATUS.map((s) => (
    <button
      key={s.v}
      data-testid={`task-status-${s.v}`}
      disabled={task.status === s.v}
      onClick={() => onStatus(task, s.v)}
      className={btnCls(task.status === s.v)}
    >
      {s.label}
    </button>
  ));
}

function CompletedInfo({ task }) {
  return (
    <div data-testid="task-completed-info" className="flex items-center gap-1.5 text-xs text-[color:var(--success)] font-semibold">
      <Lock size={12} /> Kilitli · {task.completed_by_name || task.completed_by_email || "—"} ·{" "}
      {task.completed_at ? new Date(task.completed_at).toLocaleString("tr-TR") : ""}
    </div>
  );
}

export const TaskCard = ({ task, canChange, canDelete, canReopen, onStatus, onDelete }) => {
  const meta = statusMeta(task.status);
  const overdue = isOverdue(task);
  const done = task.status === "tamamlandi";
  const showActions = done ? canReopen || canDelete : canChange || canDelete;
  return (
    <div
      data-testid="task-card"
      className={`hard border-2 bg-[color:var(--surface)] p-4 space-y-3 transition-colors ${
        overdue ? "border-[color:var(--error)]" : "border-[color:var(--border)]"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold" data-testid="task-title">{task.title}</div>
          {task.description && (
            <div className="text-sm text-[color:var(--text-2)] mt-1 whitespace-pre-wrap">{task.description}</div>
          )}
        </div>
        <span data-testid="task-status-badge" className={`shrink-0 px-2 py-0.5 hard text-[10px] font-bold uppercase ${meta.cls}`}>
          {meta.label}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[color:var(--text-2)]">
        <span className="flex items-center gap-1" data-testid="task-assignee">
          <UserIcon size={12} /> {task.assigned_to_name || task.assigned_to_email}
        </span>
        <span className={`flex items-center gap-1 ${overdue ? "text-[color:var(--error)] font-bold" : ""}`} data-testid="task-due">
          <CalendarClock size={12} /> {formatDue(task.due_date)}
          {overdue && " · GECİKMİŞ"}
        </span>
        <span className="text-[color:var(--text-3)]">Atayan: {task.assigned_by_name || task.assigned_by_email}</span>
      </div>
      {done && <CompletedInfo task={task} />}
      {showActions && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {!done && canChange && <StatusButtons task={task} onStatus={onStatus} />}
          {done && canReopen && (
            <button data-testid="task-reopen" onClick={() => onStatus(task, "devam")} className={`${btnCls(false)} flex items-center gap-1`}>
              <RotateCcw size={12} /> Geri Aç
            </button>
          )}
          {canDelete && (
            <button
              data-testid="task-delete"
              onClick={() => onDelete(task)}
              className="ml-auto p-2 hard hover:bg-[color:var(--error-bg)] hover:text-[color:var(--error)] transition-colors"
              title="Görevi sil"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
